// Derive human-readable résumé timings from "YYYY-MM" dates so durations never
// go stale — ongoing roles (endDate "present") count up to today automatically.

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const isPresent = (value) =>
  value == null || String(value).trim().toLowerCase() === "present";

// "YYYY-MM" -> absolute month index (year*12 + monthIndex); null if unparseable.
const toMonthIndex = (value) => {
  if (!value) return null;
  const [year, month] = String(value).split("-").map(Number);
  if (!year || !month || month < 1 || month > 12) return null;
  return year * 12 + (month - 1);
};

const nowMonthIndex = (now) => now.getFullYear() * 12 + now.getMonth();

const monthLabel = (value) => {
  const idx = toMonthIndex(value);
  if (idx == null) return null;
  return `${MONTHS[idx % 12]} ${Math.floor(idx / 12)}`;
};

// Inclusive month span (LinkedIn convention: Jan–May = 5 mos).
export const monthsBetween = (startDate, endDate, now = new Date()) => {
  const start = toMonthIndex(startDate);
  if (start == null) return null;
  const end = isPresent(endDate) ? nowMonthIndex(now) : toMonthIndex(endDate);
  if (end == null) return null;
  return Math.max(0, end - start + 1);
};

export const formatDuration = (months) => {
  if (months == null) return null;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "yr" : "yrs"}`);
  if (rem > 0) parts.push(`${rem} ${rem === 1 ? "mo" : "mos"}`);
  return parts.length ? parts.join(" ") : "1 mo";
};

// "May 2025 - Present" from a position's dates; null when not date-driven.
export const resolvePeriod = (position = {}) => {
  const start = monthLabel(position.startDate);
  if (!start) return null;
  const end = isPresent(position.endDate) ? "Present" : monthLabel(position.endDate);
  return end ? `${start} - ${end}` : null;
};

export const positionDuration = (position = {}, now = new Date()) =>
  formatDuration(monthsBetween(position.startDate, position.endDate, now));

// Inclusive span across a set of positions: earliest start -> latest end (today
// if any role is ongoing). Using one span — not the sum of roles — means a month
// shared by back-to-back roles (e.g. a May hand-off) is counted once, never twice.
const spanMonths = (positions = [], now = new Date()) => {
  const starts = positions.map((p) => toMonthIndex(p.startDate)).filter((v) => v != null);
  if (!starts.length) return null;
  const earliest = Math.min(...starts);
  const ongoing = positions.some((p) => isPresent(p.endDate));
  const latest = ongoing
    ? nowMonthIndex(now)
    : Math.max(...positions.map((p) => toMonthIndex(p.endDate)).filter((v) => v != null));
  return Math.max(0, latest - earliest + 1);
};

// Company duration = the span of its own positions.
export const companyDuration = (positions = [], now = new Date()) =>
  formatDuration(spanMonths(positions, now));

// Whole-career years, spanning every company's positions, rounded up.
export const totalExperienceYears = (experience = [], now = new Date()) => {
  const positions = experience.flatMap((company) => company.positions || []);
  const months = spanMonths(positions, now);
  return months == null ? null : Math.ceil(months / 12);
};
